/** Loads the chase log for active projects with a pending variation, so Home can drop variations someone has already chased. */
import { useEffect, useMemo, useState } from 'react';
import type { InterimApplication, Project } from '../../types/database';
import { listProjectChases, type InterimChaseEvent } from '../../services/InterimChaseService';
import { buildChaseList } from '../interim/chaseList';

const REFRESH_MS = 60_000;

export function useVariationChases(applications: readonly InterimApplication[], projects: readonly Project[]): InterimChaseEvent[] {
  const [events, setEvents] = useState<InterimChaseEvent[]>([]);
  // Home only shows rows for the projects it was given, so archived projects never need a request.
  const projectKey = useMemo(() => {
    const active = new Set(projects.map((p) => p.id));
    return [...new Set(buildChaseList(applications).filter((i) => i.kind === 'variation' && active.has(i.projectId)).map((i) => i.projectId))].sort().join(',');
  }, [applications, projects]);
  useEffect(() => {
    if (!projectKey) return;
    let cancelled = false;
    let latest = 0;
    const load = () => {
      // Overlapping refreshes can resolve out of order; only the newest request may set state.
      const request = ++latest;
      // The log only trims the list: a failed read leaves variations showing rather than hiding them.
      Promise.all(projectKey.split(',').map((id) => listProjectChases(id).catch(() => [])))
        .then((lists) => { if (!cancelled && request === latest) setEvents(lists.flat()); });
    };
    load();
    // A colleague may chase while Home stays open: refresh when the tab is back in view and every minute.
    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(onVisible, REFRESH_MS);
    return () => { cancelled = true; document.removeEventListener('visibilitychange', onVisible); window.clearInterval(timer); };
  }, [projectKey]);
  return events;
}
